import { Head, Link } from '@inertiajs/react';
import type { InvitesMentorsShow } from '@/types';
import { MentorPhotoCarousel } from '../components/MentorPhotoCarousel';
import { PastHacks } from '../components/PastHacks';
import { Ph26Shell } from '../components/Ph26Shell';

const REQUIREMENTS = [
  {
    day: 'Viernes 21',
    time: '17:30 – 22:30',
    title: 'Apertura',
    description:
      'Conocerás a los hackers, verás cómo los equipos eligen su track y a su mentor, y cerrarás la noche cenando con los 2-3 equipos que te eligieron. Todo dentro de la oficina.',
    optional: false,
  },
  {
    day: 'Sábado 22',
    time: 'Mínimo 3 horas',
    title: 'Acompañamiento',
    description:
      'Tú eliges el horario. Recomendamos dividirlo en un bloque AM y otro PM para hacer seguimiento a los avances de tus equipos (y ayudar a otros si te sobra tiempo).',
    optional: false,
  },
  {
    day: 'Domingo 23',
    time: '09:30 – 15:30',
    title: 'Demo day',
    description:
      'Presentaciones de los equipos, evaluación y premiación. Estás invitado a vivirlo, pero tu asistencia es opcional.',
    optional: true,
  },
] as const;

export default function Show({
  title,
  description,
  canonicalPath,
  siteUrl,
}: InvitesMentorsShow) {
  return (
    <Ph26Shell>
      <Head title={title}>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalPath} />
      </Head>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="mx-auto max-w-2xl space-y-10">
            <div className="text-center">
              <h1 className="inline-block font-bold font-sans text-2xl text-primary sm:text-3xl md:text-4xl">
                <span className="bg-primary px-3 py-2 text-background">
                  Invitación a ser mentor
                </span>
              </h1>
              <p className="mt-4 text-primary text-xl sm:text-2xl md:text-3xl">
                <Link
                  href="/26-co"
                  className="lowercase transition-opacity hover:opacity-85"
                >
                  <span className="font-logo tracking-tight">
                    platanus hack [26]
                  </span>
                  <span className="font-title uppercase tracking-normal">
                    : bogotá
                  </span>
                </Link>
              </p>
              <p className="mt-2 font-mono text-primary/70 text-sm md:text-base">
                21-23 de agosto, 2026 • Bogotá, Colombia
              </p>
            </div>

            <section className="space-y-3 text-center">
              <p className="text-sm sm:text-base">
                Del 21 al 23 de agosto tendremos Platanus Hack 26: Bogotá ft.
                Buk. Tendremos a 100 ingenieros, devs y hackers crack,
                construyendo un producto desde cero en 36 horas, con el auspicio
                de Anthropic, ElevenLabs, Vercel, Supabase, Render y más.{' '}
                <span className="bg-primary px-1 text-background">
                  Nos encantaría que fueras mentor/a.
                </span>
              </p>
              <p className="text-muted-foreground text-sm sm:text-base">
                Como mentor apadrinarás a 2-3 equipos desde el brainstorming
                hasta que su producto esté listo, compartiendo tu experiencia
                para que construyan algo increíble en 36 horas.
              </p>
              <p className="text-muted-foreground text-sm sm:text-base">
                Además, estarás{' '}
                <strong className="text-primary">
                  expuesto a 100 de los hackers con mejor nivel de Bogotá
                </strong>
                : una gran oportunidad para{' '}
                <strong className="text-primary">
                  conocer talento y hacer contrataciones
                </strong>
                .
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-center font-bold text-sm uppercase tracking-wide">
                Algunas fotos de las cenas de mentores de las últimas hackatones
              </h2>
              <MentorPhotoCarousel />
            </section>

            <section className="space-y-4">
              <h2 className="text-center font-bold text-sm uppercase tracking-wide">
                Lo que necesitamos de ti
              </h2>
              <div className="space-y-3">
                {REQUIREMENTS.map((item) => (
                  <div
                    key={item.day}
                    className={
                      item.optional
                        ? 'border-2 border-primary/40 border-dashed bg-background/50 p-5'
                        : 'border-2 border-primary bg-background/80 p-5'
                    }
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <p className="font-bold uppercase tracking-wide">
                        {item.day} — {item.title}
                        {item.optional && (
                          <span className="ml-2 border border-primary/50 px-1.5 py-0.5 font-mono text-[10px] text-primary/70 normal-case tracking-normal">
                            Opcional
                          </span>
                        )}
                      </p>
                      <p
                        className={
                          item.optional
                            ? 'bg-primary/40 px-2 py-0.5 font-mono font-semibold text-background/90 text-xs uppercase tracking-wide'
                            : 'bg-primary px-2 py-0.5 font-mono font-semibold text-background text-xs uppercase tracking-wide'
                        }
                      >
                        {item.time}
                      </p>
                    </div>
                    <p className="mt-2 text-muted-foreground text-sm sm:text-base">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <div className="border-primary/30 border-t" />

            <PastHacks siteUrl={siteUrl} />

            <p className="text-center text-muted-foreground text-sm">
              ¿Te sumas como mentor? Responde el mensaje que te llegó por
              WhatsApp y coordinamos todo.
            </p>
          </div>
        </div>
      </div>
    </Ph26Shell>
  );
}
